import type { ErrorEvent as SentryErrorEvent } from '@sentry/react';

/**
 * Matches an error message that is nothing but one to three letters, e.g. `Aa`,
 * `Ba`, `Vd`, `he`.
 */
const MANGLED_TOKEN_MESSAGE = /^[A-Za-z]{1,3}$/;

/**
 * Google's iOS apps -- Chrome for iOS and the Google Search in-app browser --
 * run their own minified page-world scripts inside our page. When one of those
 * rejects a promise it reaches our `unhandledrejection` handler as a genuine
 * Error whose message is a single mangled token (`Aa`, `Ba`, `he`, ...) and
 * which carries no stack at all. Over the last 90 days every single one of
 * these events came from `Chrome Mobile iOS` or the `Google` in-app browser on
 * iOS -- no Safari, no Android, no desktop -- and the same message shows up
 * both across a dozen of our releases and in the separately built twint bundle,
 * so it cannot originate in our code.
 *
 * They cannot be filtered by frame-based means, because having no frames is
 * precisely why they get through: `allowUrls` derives its URL from the stack
 * frames and treats a frameless event as allowed (`!url ? true`), and
 * `thirdPartyErrorFilterIntegration` no-ops when it finds no frames to
 * classify. Both guards drop the same errors when they *do* carry frames, which
 * is why only the frameless ones survive.
 *
 * Google re-mints these names on its own release schedule, so every rotation
 * opens a brand new Sentry issue and reads as a fresh regression. Hence a match
 * on the shape rather than an enumeration of the names, which would go stale by
 * design.
 *
 * Deliberately narrow -- an unhandled rejection AND no frames AND a message
 * that is only a mangled token. A short message that does carry a stack still
 * reports, so a genuine minified failure from a third party we inject ourselves
 * (Cloudflare Turnstile on the login and sign-up paths) is not swallowed.
 *
 * Added 2026-08-20, see Sentry CUSTOMER-PROD-DWM / -DVG / -DW9.
 */
export function isHostInjectedScriptNoise(event: SentryErrorEvent): boolean {
    const values = event.exception?.values;
    // A chained exception is never this noise, so require the single-value shape
    // rather than guessing which entry was thrown.
    if (values?.length !== 1) {
        return false;
    }
    const [exception] = values;
    return (
        exception.mechanism?.type === 'onunhandledrejection' &&
        !exception.stacktrace?.frames?.length &&
        MANGLED_TOKEN_MESSAGE.test(exception.value ?? '')
    );
}
